import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import SEO from './SEO';
import { PAGE_SEO, buildBreadcrumbs } from '../utils/seoConfig';
import './CoachesPage.css';

const THEME_ACCENTS = {
  classic: '#48bb78',
  modern: '#38b2ac',
  warm: '#ed8936',
  fresh: '#38a169',
  dark: '#a78bfa',
};

const CoachesPage = () => {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebounce(searchTerm, 300);

  useEffect(() => {
    fetchCoaches();
  }, [debouncedSearch]);

  const fetchCoaches = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/fitness/coaches?${params}`);
      if (response.ok) {
        const data = await response.json();
        setCoaches(data.coaches || []);
      }
    } catch (error) {
      console.error('Error fetching coaches:', error);
    } finally {
      setLoading(false);
    }
  };

  const seo = PAGE_SEO['/coaches'];

  return (
    <div className="coaches-page">
      <SEO
        title={seo.title}
        description={seo.description}
        canonical="/coaches"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: seo.title,
            description: seo.description,
            url: 'https://freshfitlab.io/coaches',
          },
          buildBreadcrumbs([
            { name: 'Home', url: '/' },
            { name: 'Coaches', url: '/coaches' },
          ]),
        ]}
      />
      <div className="coaches-hero">
        <h1><i className="fas fa-dumbbell"></i> Fitness Coaches</h1>
        <p>Find expert coaches to guide your fitness journey</p>
      </div>

      <div className="coaches-cta-row">
        <div className="coaches-cta-card">
          <div className="coaches-cta-copy">
            <h2>Want to coach on FreshFitLab?</h2>
            <p>Create your public coach page, publish programs, and start working with clients.</p>
          </div>
          <Link to="/affiliate/register" className="coaches-cta-link">
            Become a Coach / Creator
          </Link>
        </div>
      </div>

      <div className="coaches-search">
        <i className="fas fa-search"></i>
        <input
          type="text"
          placeholder="Search coaches..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button className="coaches-search-clear" onClick={() => setSearchTerm('')}>
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>

      {loading ? (
        <div className="coaches-loading">
          <i className="fas fa-spinner fa-spin coaches-spinner"></i>
          <p>Loading coaches...</p>
        </div>
      ) : coaches.length === 0 ? (
        <div className="coaches-empty">
          <i className="fas fa-user-slash"></i>
          <h3>No coaches found</h3>
          <p>{searchTerm ? 'Try a different search term' : 'Check back soon for new coaches!'}</p>
        </div>
      ) : (
        <div className="coaches-grid">
          {coaches.map(coach => {
            const accent = THEME_ACCENTS[coach.theme] || THEME_ACCENTS.classic;
            return (
              <Link to={`/coach/${coach.slug}`} key={coach._id} className="coach-card">
                <div className="coach-card-accent" style={{ background: accent }} />
                <div className="coach-card-content">
                  {coach.logoImage ? (
                    <img src={coach.logoImage} alt={coach.name} className="coach-card-avatar" />
                  ) : (
                    <div className="coach-card-avatar-placeholder" style={{ background: accent }}>
                      {(coach.name || '?')[0].toUpperCase()}
                    </div>
                  )}
                  <h3 className="coach-card-name">{coach.name}</h3>
                  {coach.headline && <p className="coach-card-headline">{coach.headline}</p>}
                  <div className="coach-card-stats">
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CoachesPage;
